/* Canonical purchase-option presentation. No network requests or cart/contract mutations. */
(() => {
  'use strict';
  const key = '__mmPurchaseOptionsV4';
  if (window[key]) { window[key].initialize(); return; }

  const controllers = new WeakMap();
  const integerAmount = value => typeof value === 'string' && /^\d+$/.test(value) && Number.isSafeInteger(Number(value)) ? Number(value) : null;
  const isDisplayNode = node => !node.matches('option, input, select') && !node.closest('[data-mm-purchase-gates]');
  const unavailableLabel = (root, node) => root.dataset.mmClubUnavailableLabel || node?.dataset.mmUnavailableLabel || root.dataset.mmPlanUnavailableLabel || 'Unavailable';
  const schemaRecords = root => [...document.querySelectorAll('script[data-mm-product-schema]')].filter(node => node.dataset.mmProductSchema === root.dataset.mmProductId);
  const gateFor = (root, variantId) => [...root.querySelectorAll('[data-mm-variant-gate]')].find(node => node.dataset.mmVariantGate === variantId) || null;
  const markCanonicalReady = root => {
    delete root.dataset.mmCanonicalPending;
    root.dataset.mmCanonicalReady = 'true';
  };

  function baseOptionLabel(option) {
    if (!option || option.tagName !== 'OPTION') return '';
    if (option.dataset.mmBaseLabel) return option.dataset.mmBaseLabel;
    let label = (option.textContent || '').trim();
    label = label.replace(/\s+(?:—|–|-)\s*(?:Sold out|Agotado|Unavailable|No disponible|Details being verified|Detalles en verificación)\s*$/i, '').trim();
    [option.dataset.mmPrice, option.dataset.price].filter(Boolean).forEach(price => {
      const index = label.lastIndexOf(price);
      if (index >= 0) label = label.slice(0, index).trim();
    });
    label = label.replace(/\s*(?:—|–|-)\s*$/, '').trim();
    option.dataset.mmBaseLabel = label || option.value;
    return option.dataset.mmBaseLabel;
  }

  function clearVariantNode(root, node, label = '') {
    if (!node) return;
    const unavailable = unavailableLabel(root, node);
    node.dataset.mmAmount = '';
    node.dataset.mmPrice = '';
    node.dataset.mmComparePrice = '';
    node.dataset.mmAvailable = 'false';
    node.dataset.mmHoldReason = node.dataset.mmHoldReason || 'variant';
    node.dataset.price = '';
    node.dataset.comparePrice = '';
    node.dataset.available = 'false';
    if (node.tagName === 'OPTION') node.textContent = `${label || baseOptionLabel(node)} — ${unavailable}`;
  }

  function hydrateVariantNode(root, node) {
    if (!node) return;
    const label = node.tagName === 'OPTION' ? baseOptionLabel(node) : '';
    const gate = gateFor(root, node.value);
    if (!gate) {
      clearVariantNode(root, node, label);
      return;
    }
    const price = gate.dataset.mmPrice || '';
    const priceCents = gate.dataset.mmPriceCents || '';
    const comparePrice = gate.dataset.mmComparePrice || '';
    const available = gate.dataset.mmAvailable === 'true';
    node.dataset.mmAmount = priceCents;
    node.dataset.mmPrice = price;
    node.dataset.mmComparePrice = comparePrice;
    node.dataset.mmAvailable = String(available);
    node.dataset.mmHoldReason = gate.dataset.mmHoldReason || '';
    node.dataset.price = price;
    node.dataset.comparePrice = comparePrice;
    node.dataset.available = String(available);
    if (node.tagName === 'OPTION') {
      const unavailable = unavailableLabel(root, node);
      const detail = price || unavailable;
      node.textContent = `${label} — ${detail}${!available && price ? ` — ${unavailable}` : ''}`;
    }
  }

  function setActivePlanGroup(picker, variantId, required, previousPlan, variantChanged, defaultToOneTime) {
    if (!picker) return null;
    let active = null;
    picker.querySelectorAll('[data-mm-plan-variant]').forEach(group => {
      const selected = group.dataset.mmPlanVariant === variantId;
      group.hidden = !selected;
      group.querySelectorAll('input[name="selling_plan"]:not([data-sls-selling_plan]), select[name="selling_plan"]:not([data-sls-selling_plan])').forEach(control => {
        if (control.disabled !== !selected) control.disabled = !selected;
      });
      if (selected) active = group;
    });
    if (!active) return null;

    const radios = [...active.querySelectorAll('input[type="radio"][name="selling_plan"]:not(:disabled)')];
    const select = active.querySelector('select[name="selling_plan"]:not(:disabled)');
    if (select) {
      if (variantChanged || defaultToOneTime) {
        const matching = [...select.options].find(option => !option.disabled && option.value === previousPlan);
        if (matching) select.value = matching.value;
        else if (!required) select.value = '';
        else {
          const index = [...select.options].findIndex(option => !option.disabled && option.value);
          if (index >= 0) select.selectedIndex = index;
        }
      }
      return active;
    }

    const checked = radios.find(input => input.checked);
    if (!checked || variantChanged || defaultToOneTime) {
      const matching = radios.find(input => input.value === previousPlan);
      const oneTime = radios.find(input => input.value === '');
      const firstPlan = radios.find(input => input.value !== '');
      const next = matching || (required ? firstPlan : oneTime || firstPlan);
      if (next && next !== checked) {
        next.checked = true;
        next.dispatchEvent(new Event('change', {bubbles: true}));
      }
    }
    return active;
  }

  // These inert records are emitted only for canonically admitted allocations.
  // Keep the policy and amounts independent of app-replaced presentation controls.
  function readPlanEvidence(root, form) {
    const containers = [...form.querySelectorAll('[data-mm-plan-evidence]')]
      .filter(node => node.dataset.mmPlanProduct === root.dataset.mmProductId && node.closest('form') === form);
    const evidence = containers.length === 1 ? containers[0] : null;
    return {
      present: containers.length > 0,
      required: containers.some(node => node.dataset.mmRequired === 'true'),
      allocations: evidence ? [...evidence.querySelectorAll('[data-mm-plan-allocation]')].map(node => ({
        variantId: node.dataset.mmPlanVariant || '',
        planId: node.dataset.mmPlanId || '',
        amount: integerAmount(node.dataset.mmPlanAmount || ''),
        formatted: node.dataset.mmPlanPrice || '',
        available: node.dataset.mmPlanAvailable === 'true'
      })) : []
    };
  }

  function readPlan(active, gate, form, variantId, allocations) {
    const submitted = [...form.elements].filter(control => control.form === form && control.isConnected &&
      control.name === 'selling_plan' && !control.matches(':disabled') &&
      (!['radio', 'checkbox'].includes(control.type) || control.checked));
    const hidden = submitted.filter(control => control.matches('input[type="hidden"]'));
    if (hidden.length) {
      // Seal owns transport only. Never infer its price, fill its value, or accept
      // ambiguous form payloads; the exact variant/allocation remains authoritative.
      const control = submitted.length === 1 && hidden.length === 1 &&
        hidden[0].matches('[data-sls-selling_plan]') ? hidden[0] : null;
      const matching = control ? allocations.filter(record =>
        record.variantId === variantId && record.planId === control.value) : [];
      const record = matching.length === 1 ? matching[0] : null;
      return {
        control: record ? control : null,
        planId: record ? control.value : '',
        amount: record?.amount ?? null,
        formatted: record?.formatted || '',
        available: Boolean(record?.available && record.formatted)
      };
    }

    const select = active?.querySelector('select[name="selling_plan"]:not(:disabled)');
    if (select) {
      const option = select.selectedOptions[0];
      if (!option || option.disabled) return {control: null, planId: '', amount: null, formatted: '', available: false};
      return {
        control: option,
        planId: option.value || '',
        amount: integerAmount(option.dataset.planAmount || option.dataset.mmAmount || ''),
        formatted: option.dataset.planPrice || option.dataset.mmPlanPrice || '',
        available: option.dataset.planAvailable !== 'false' && option.dataset.mmPlanAvailable !== 'false'
      };
    }

    const input = active?.querySelector('input[name="selling_plan"]:checked:not(:disabled)');
    if (input) {
      return {
        control: input,
        planId: input.value || '',
        amount: integerAmount(input.dataset.planAmount || input.dataset.mmAmount || ''),
        formatted: input.dataset.planPrice || input.dataset.mmPlanPrice || '',
        available: input.dataset.planAvailable !== 'false' && input.dataset.mmPlanAvailable !== 'false'
      };
    }

    return {
      control: null,
      planId: '',
      amount: gate ? integerAmount(gate.dataset.mmPriceCents || '') : null,
      formatted: gate?.dataset.mmPrice || '',
      available: Boolean(gate)
    };
  }

  // Update only this page's owned price tags; a quick view must not alter another product's head.
  function syncSocialPrice(root, amount) {
    const context = document.head.querySelector('meta[name="mm-product-context"]');
    if (!context || context.content !== root.dataset.mmProductId) return;
    try {
      const pageProduct = new URL(context.dataset.mmProductUrl, location.origin);
      const rootProduct = new URL(root.dataset.mmProductUrl, location.origin);
      if (pageProduct.origin !== location.origin || rootProduct.origin !== location.origin ||
          rootProduct.pathname !== pageProduct.pathname || !location.pathname.endsWith(pageProduct.pathname)) return;
      const currency = context.dataset.mmProductCurrency || '';
      const tags = ['product:price:amount', 'product:price:currency'];
      const values = Number.isSafeInteger(amount) && amount >= 0 && /^[A-Z]{3}$/.test(currency)
        ? [(amount / 100).toFixed(2), currency] : null;
      tags.forEach((property, index) => {
        const nodes = [...document.head.querySelectorAll(`meta[data-mm-product-price][property="${property}"]`)];
        if (!values) { nodes.forEach(node => node.remove()); return; }
        const node = nodes.shift() || document.createElement('meta');
        node.setAttribute('property', property);
        node.setAttribute('data-mm-product-price', '');
        node.content = values[index];
        if (!node.isConnected) document.head.append(node);
        nodes.forEach(duplicate => duplicate.remove());
      });
    } catch (_) { /* Metadata must never prevent product selection or checkout. */ }
  }

  function initializeRoot(root) {
    if (controllers.has(root)) return;
    const form = root.querySelector('.mm-product-form');
    const variantInput = form?.querySelector('[name="id"]');
    if (!form || !variantInput) {
      markCanonicalReady(root);
      return;
    }

    const picker = form.querySelector('[data-mm-plan-picker]');
    const planEvidence = readPlanEvidence(root, form);
    const required = picker?.dataset.mmRequired === 'true' || planEvidence.required;
    const hasPlanPicker = Boolean(picker) || planEvidence.present;
    const variantNodes = variantInput.tagName === 'SELECT' ? [...variantInput.options] : [variantInput];
    variantNodes.forEach(node => hydrateVariantNode(root, node));
    let previousVariant = null;
    let previousPlan = '';
    let disposed = false;
    const requestedPlan = new URLSearchParams(location.search).get('selling_plan') || '';

    function sync(updateAddress = false) {
      if (disposed || !root.isConnected) return false;
      const variant = variantInput.tagName === 'SELECT' ? variantInput.selectedOptions[0] : variantInput;
      const variantId = variantInput.value;
      hydrateVariantNode(root, variant);
      const gate = gateFor(root, variantId);
      const gateReason = gate?.dataset.mmHoldReason || '';
      const gateAvailable = gate?.dataset.mmAvailable === 'true';
      const held = Boolean(gateReason) || !gate;
      const variantChanged = previousVariant !== null && previousVariant !== variantId;
      const defaultToOneTime = !required && previousVariant === null && !requestedPlan;
      const active = setActivePlanGroup(form.querySelector('[data-mm-plan-picker]'), variantId, required, previousPlan, variantChanged, defaultToOneTime);
      const plan = readPlan(active, gate, form, variantId, planEvidence.allocations);
      const blocked = hasPlanPicker && required && (!plan.control || !plan.planId);
      const amount = blocked ? null : plan.amount;
      const formatted = blocked ? '' : plan.formatted;
      const unavailable = unavailableLabel(root, variant);
      const available = Boolean(variant) && Boolean(gate) && gateAvailable && !held && !blocked && plan.available && amount !== null;
      const offerEligible = Boolean(variant) && Boolean(gate) && !blocked && amount !== null && !['preorder', 'selling-plan', 'variant'].includes(gateReason);
      const display = amount === null ? unavailable : formatted;

      if (display !== undefined) {
        root.querySelectorAll('[data-mm-product-price], [data-mm-sticky-price], [data-mm-price]').forEach(node => {
          if (!isDisplayNode(node)) return;
          node.textContent = display;
        });
      }
      root.querySelectorAll('[data-mm-club-price]').forEach(node => {
        node.textContent = amount === null
          ? unavailable
          : `${formatted} ${node.dataset.mmClubPriceSuffix || ''}`.trim();
      });
      root.querySelectorAll('[data-mm-compare-price]').forEach(node => {
        if (!isDisplayNode(node)) return;
        const compare = available ? (gate?.dataset.mmComparePrice || '') : '';
        node.textContent = compare;
        node.hidden = !compare;
      });

      const status = root.querySelector('[data-mm-availability]');
      if (status) {
        status.textContent = blocked || amount === null || held
          ? unavailable
          : available ? root.dataset.mmAvailableLabel : root.dataset.mmSoldLabel;
        status.classList.toggle('mm-product-availability--sold', !available);
        status.classList.toggle('is-sold-out', !available);
      }

      form.querySelectorAll('button[type="submit"], input[type="submit"]').forEach(button => {
        button.disabled = !available;
        const label = button.querySelector('[data-mm-purchase-label]');
        if (label) label.textContent = available
          ? (!required && !plan.planId ? (label.dataset.mmOneTimeLabel || label.dataset.mmReadyLabel) : label.dataset.mmReadyLabel)
          : label.dataset.mmUnavailableLabel;
      });
      const payment = form.querySelector('[data-mm-payment-wrap]');
      if (payment) payment.hidden = !available;

      schemaRecords(root).forEach(script => {
        try {
          const graph = JSON.parse(script.textContent);
          const item = graph['@graph']?.find(node => node['@type'] === 'Product');
          if (!item || !item.url) return;
          const sku = variant?.dataset.mmSku || '';
          if (sku) item.sku = sku; else delete item.sku;
          ['gtin8', 'gtin12', 'gtin13', 'gtin14'].forEach(schemaKey => delete item[schemaKey]);
          const gtin = variant?.dataset.mmGtin || '';
          if (/^(?:\d{8}|\d{12}|\d{13}|\d{14})$/.test(gtin)) item[`gtin${gtin.length}`] = gtin;

          if (!offerEligible || !script.dataset.mmOfferTemplate) {
            delete item.offers;
          } else {
            const offer = JSON.parse(script.dataset.mmOfferTemplate);
            const url = new URL(item.url, location.origin);
            url.searchParams.set('variant', variantId);
            url.searchParams.delete('selling_plan');
            if (plan.planId) url.searchParams.set('selling_plan', plan.planId);
            item.offers = {...offer, url: url.href, price: amount / 100, availability: available ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock'};
          }
          script.textContent = JSON.stringify(graph).replace(/</g, '\\u003c');
        } catch (_) { /* A schema problem must never break purchase controls. */ }
      });

      syncSocialPrice(root, offerEligible ? amount : null);

      if (updateAddress && variant) {
        try {
          const current = new URL(location.href);
          const productPath = new URL(root.dataset.mmProductUrl, current.origin).pathname;
          if (current.pathname.endsWith(productPath)) {
            current.searchParams.set('variant', variantId);
            if (plan.planId) current.searchParams.set('selling_plan', plan.planId);
            else current.searchParams.delete('selling_plan');
            history.replaceState(history.state, '', current.href);
          }
        } catch (_) { /* Preview restrictions must not break buying. */ }
      }

      previousVariant = variantId;
      previousPlan = plan.planId;
      return available;
    }

    let syncQueued = false;
    let addressQueued = false;
    const scheduleSync = (updateAddress = false) => {
      addressQueued = addressQueued || updateAddress;
      if (syncQueued) return;
      syncQueued = true;
      queueMicrotask(() => {
        const update = addressQueued;
        syncQueued = false;
        addressQueued = false;
        sync(update);
      });
    };
    const onChange = event => {
      if (event.target.matches('[name="id"], [name="selling_plan"]')) scheduleSync(true);
    };
    const planControlSelector = 'input[name="selling_plan"], select[name="selling_plan"]';
    const containsPlanControl = node => node.nodeType === 1 &&
      (node.matches(planControlSelector) || Boolean(node.querySelector(planControlSelector)));
    const planObserver = typeof MutationObserver === 'function' ? new MutationObserver(mutations => {
      if (mutations.some(mutation => mutation.type === 'attributes'
        ? containsPlanControl(mutation.target) || (mutation.attributeName === 'name' && mutation.oldValue === 'selling_plan')
        : [...mutation.addedNodes, ...mutation.removedNodes].some(containsPlanControl))) scheduleSync();
    }) : null;
    const onSubmit = event => {
      if (!sync()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (required) {
          const activeControl = form.querySelector('[data-mm-plan-picker]')?.querySelector('[data-mm-plan-variant]:not([hidden]) input[name="selling_plan"]:not(:disabled), [data-mm-plan-variant]:not([hidden]) select[name="selling_plan"]:not(:disabled)');
          activeControl?.reportValidity?.();
          activeControl?.focus?.({preventScroll: false});
        }
      }
    };

    form.addEventListener('change', onChange);
    form.addEventListener('input', onChange);
    form.addEventListener('submit', onSubmit, true);
    planObserver?.observe(form, {
      childList: true, subtree: true, attributes: true, attributeOldValue: true,
      attributeFilter: ['value', 'name', 'type', 'form', 'disabled', 'checked']
    });
    controllers.set(root, () => {
      disposed = true;
      form.removeEventListener('change', onChange);
      form.removeEventListener('input', onChange);
      form.removeEventListener('submit', onSubmit, true);
      planObserver?.disconnect();
      controllers.delete(root);
    });
    sync();
    markCanonicalReady(root);
  }

  function initialize(scope = document) {
    if (scope.matches?.('[data-mm-product-root][data-mm-product-id]')) initializeRoot(scope);
    scope.querySelectorAll?.('[data-mm-product-root][data-mm-product-id]').forEach(initializeRoot);
  }

  window[key] = {initialize};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), {once: true});
  else initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    if (event.target.matches?.('[data-mm-product-root]')) controllers.get(event.target)?.();
    event.target.querySelectorAll?.('[data-mm-product-root]').forEach(root => controllers.get(root)?.());
  });
})();



